<script setup lang="ts">
import { mdiChevronDown, mdiChevronLeft, mdiSortAlphabeticalVariant } from "@mdi/js";
import { storeToRefs } from "pinia";
import AToZMenu from "~/components/layout/AToZMenu/AToZMenu.vue";
import SidebarFeeds from "~/components/layout/Feeds/SidebarFeeds.vue";
import Search from "~/components/layout/Search/Search.vue";
import RecentWinners from "~/components/layout/Sport/RecentWinners/index.vue";
import Icon from "~/components/ui/Icon.vue";
import useFeeds from "~/composables/Feeds/useFeeds";
import UseCollapsedView from "~/composables/useCollapsedView";
import { useAuth } from "~/store/auth";
import { useBetslip } from "~/store/betslip";
import { useSettings } from "~/store/settings";
import { isMobile } from "~/utils/constants";
import t from "~/utils/translate";
import PromotionalBanner from "../Casino/Widget/PromotionalBanner.vue";
import Betslip from "./Betslip/index.vue";

interface Props {
	class?: string | string[];
	hasSearch?: boolean;
	contentView?: boolean;
	isToteView?: boolean;
	title?: string;
	isLandingView?: boolean;
	isSingleColumn?: boolean;
}
const props = withDefaults(defineProps<Props>(), { hasSearch: true });
const betslip = useBetslip();
const settings = useSettings();
const auth = useAuth();
const router = useRouter();
const isCollapsedView = UseCollapsedView();

onMounted(() => {
	if (!isMobile && (betslip.selection.length || betslip.buildSelections.length)) {
		betslip.open = true;
	}
});

const showFeedsAndFollowings = computed(() => {
	return settings.hasFeature("EnableFeedsAndFollow") && auth.loggedIn && !isMobile;
});

const { hasError } = useFeeds();
const { azMenu } = storeToRefs(settings);
const aToZMenu = ref();

router.beforeEach((to, from, next) => {
	// Check if the AToZ menu is open
	if (azMenu.value) {
		azMenu.value = false; // Set the menu to closed on route change
		settings.azMenu = false;
	}
	next();
});
</script>

<template>
	<div class="w-full" v-if="isToteView || isSingleColumn">
		<slot />
	</div>
	<div class="w-full" v-else-if="isLandingView">
		<div class="absolute container left-0 right-0">
			<div class="betbook-grid">
				<div class="betbook-grid-item"></div>
				<div class="betbook-grid-item"></div>
				<div class="betbook-grid-item relative">
					<div
						class="absolute top-0 left-0 w-full h-[auto] max-h-[calc(100vh-170px)] overflow-y-auto transition-all"
						:class="{ 'opacity-1 z-20': betslip.open, 'opacity-0 -z-10': !betslip.open }"
					>
						<ClientOnly>
							<Betslip v-if="!isMobile" />
						</ClientOnly>
					</div>
				</div>
			</div>
		</div>
		<slot />
	</div>
	<div v-else>
		<div v-if="title" class="flex h-10 w-full bg-dark-700 md:hidden p-1 items-center">
			<div class="bg-dark-600 h-full w-fit rounded relative flex items-center justify-center cursor-pointer" @click="router.back()">
				<Icon :path="mdiChevronLeft" class="fill-light-50 h-6 w-6" />
				<strong class="text-xs pr-2 cursor-pointer text-light-50">{{ t("back") }}</strong>
			</div>
			<div class="h-5 bg-dark-500 w-[1px] mx-1" />
			<div class="bg-dark-600 font-bold text-xs px-3 h-full w-fit rounded relative flex items-center justify-center text-light-50">
				{{ title }}
			</div>
		</div>
		<div
			class="relative"
			:class="[contentView ? 'betbook-content-container betbook-grid' : 'container betbook-grid', isCollapsedView ? 'pt-0' : 'pt-2']"
		>
			<div class="betbook-grid-item item-left">
				<slot name="left">
					<div class="sticky top-40 flex gap-1 flex-col overflow-y-scroll overflow-x-hidden h-[calc(100vh-170px)] scrollbar-hidden">
						<DataFreeGuard>
							<RecentWinners v-if="settings.hasFeature('IsShowBigWinnersWidgetEnabled')" />
						</DataFreeGuard>
					</div>
				</slot>
			</div>
			<div :class="['betbook-grid-item', props.class]">
				<div
					class="z-30 hidden gap-2 py-2 flex-nowrap md:flex sticky -mt-2 left-0 bg-light-200 dark:bg-dark-900"
					:class="settings.isDataFree ? 'md:top-[150px] lg:top-[192px]' : 'md:top-[110px] lg:top-[152px]'"
					v-show="hasSearch && !isCollapsedView"
				>
					<div
						:class="[
							'align-items-center cursor-pointer px-3 py-2 overflow-hidden relative bg-dark-800 text-light-50 rounded leading hidden md:inline-flex font-bold ',
							{ 'cursor-wait': !aToZMenu || aToZMenu.isSportsLoading || aToZMenu.isSortedSportsLoading },
						]"
						@click="azMenu = !azMenu"
					>
						<UiIcon :path="mdiSortAlphabeticalVariant" class="w-5 h-5 fill-identity" />
						<span class="mx-2">{{ t("a-to-z-sports") }}</span>
						<UiIcon :path="mdiChevronDown" class="w-5 h-5 fill-light-50" />
					</div>
					<AToZMenu ref="aToZMenu"></AToZMenu>
					<Search />
				</div>
				<slot />
			</div>
			<div class="relative flex-col content-start hidden betbook-grid-item xl:flex">
				<div :class="['sticky left-0 flex top-40 h-[calc(100vh-170px)] overflow-x-hidden flex-col gap-2']">
					<div
						class="absolute top-0 left-0 w-full h-[auto] transition-all"
						:class="{ 'opacity-1 z-20': betslip.open, 'opacity-0 -z-10': !betslip.open }"
					>
						<ClientOnly>
							<Betslip v-if="!isMobile" />
						</ClientOnly>
					</div>
					<DataFreeGuard>
						<SidebarFeeds v-if="showFeedsAndFollowings && auth.hydrated && !hasError" />
						<PromotionalBanner v-else-if="auth.hydrated || hasError" />
					</DataFreeGuard>
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.betbook-grid {
	@apply grid-cols-12 gap-2 pb-6 grid;
}

@media (max-width: 1280px) {
	.betbook-grid-item {
		@apply col-span-12;

		&.item-left {
			display: none;
		}
	}
}

.betbook-content-container {
	@apply px-2 mx-auto md:px-4;
}

@media (min-width: 1281px) {
	.betbook-grid {
		grid-template-columns: 340px calc(100% - 686px) 330px;
	}
}
</style>
